// Typ: FindArcLengthFromAngleTask
// Temat: Okrąg i koło → Elementy, długości i pola → Długość łuku
// Wzór:  ℓ = (α/360°) · 2πr = (α·r/180) · π
// Odpowiedź: pokazujemy ułamek mieszany (jeśli trzeba), np. 11\frac{2}{3}\pi

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import PiInput, { parsePiLikeInput, formatPiValueLatex } from "../../../../components/inputs/PiInput";

const DEC_TOL = 0.1;

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

/** zapis k*pi jako latex: całkowita + ułamek właściwy */
function formatMixedTimesPi(n, d) {
  // n/d jest już skrócone
  const q = Math.floor(n / d);
  const r = n % d;

  if (r === 0) {
    // całkowita wielokrotność pi
    return q === 1 ? "\\pi" : `${q}\\,\\pi`;
  }
  if (q === 0) {
    // ułamek właściwy
    return `\\dfrac{${r}}{${d}}\\,\\pi`;
  }
  // ułamek mieszany
  return `${q}\\,\\dfrac{${r}}{${d}}\\,\\pi`;
}

// ===== generator =====
export function generateTask() {
  const r = Math.floor(Math.random() * 9) + 2; // 2..10

  // kąty co 15° + wielokrotności 40°; usuwamy duplikaty
  const ANGLES_15 = Array.from({ length: 24 }, (_, i) => 15 * (i + 1));   // 15..360
  const ANGLES_40 = Array.from({ length: 9  }, (_, i) => 40 * (i + 1));   // 40..360

  // unikalne i bez 360°
  const ALL_ANGLES = [...new Set([...ANGLES_15, ...ANGLES_40])].filter(a => a !== 360);

  const alpha = ALL_ANGLES[Math.floor(Math.random() * ALL_ANGLES.length)];
  return { type: "FindArcLengthFromAngleTask", data: { r, alpha } };
}

// ===== render task =====
export function renderTask({ data }) {
  const { r, alpha } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      Dany jest okrąg o promieniu <InlineMath math={`r=${r}`} /> oraz
      kąt środkowy <InlineMath math={`\\alpha=${alpha}^{\\circ}`} />.
      Oblicz długość łuku <InlineMath math={'\\ell'} />.
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <PiInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={'\\ell'}
      placeholder="np. 5π/3, π, 10.5 (±0.1)"
    />
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { r, alpha } = data;
  const expected = (alpha / 360) * 2 * Math.PI * r;

  const parsed =
    typeof userAnswer === "object" && userAnswer?.parsed != null
      ? userAnswer.parsed
      : parsePiLikeInput(typeof userAnswer === "object" ? userAnswer.raw : userAnswer);

  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - expected) <= DEC_TOL;
}

// ===== answer (dokładnie: ułamek mieszany razy π) =====
export function renderAnswer({ data }) {
  const { r, alpha } = data;
  const num0 = alpha * r;     // α·r
  const den0 = 180;
  const g = gcd(num0, den0);
  const n = num0 / g, d = den0 / g;

  const exact = formatMixedTimesPi(n, d);
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`\\ell = ${exact}`} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { r, alpha } = data;
  const approx = (((alpha / 360) * 2 * Math.PI * r)).toFixed(2);
  const g = gcd(alpha * r, 180);
  const n = (alpha * r) / g, d = 180 / g;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wzór na długość łuku:</div>
      <BlockMath math={'\\ell = \\dfrac{\\alpha}{360^{\\circ}}\\cdot 2\\pi r'} />

      <div><strong>Krok 2.</strong> Podstawiamy <InlineMath math={`r=${r}`} /> i <InlineMath math={`\\alpha=${alpha}^{\\circ}`} />:</div>
      <BlockMath math={`\\ell = \\dfrac{${alpha}}{360}\\cdot 2\\pi\\cdot ${r} = \\dfrac{${alpha}\\cdot ${r}}{180}\\,\\pi`} />

      <div><strong>Krok 3.</strong> Uproszczenie ułamka:</div>
      <BlockMath math={`\\ell = ${formatMixedTimesPi(n, d)}`} />

      <div><strong>Krok 4.</strong> (Opcjonalnie) Przybliżenie:</div>
      <BlockMath math={`\\ell \\approx ${approx}`} />
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`\\ell = ${formatPiValueLatex(raw) || ""}`} />
    </div>
  );
}
